import { Injectable, inject } from '@angular/core';
import { limit, orderBy, where } from '@angular/fire/firestore';
import { COLLECTIONS, ENVIRONMENT } from '../core.constants';
import { Id } from '../models/common.model';
import { ChatGroup, Edition, Stage } from '../models/edition.model';
import { MessageScheduled, MessageSended, MessageWithSchedule } from '../models/message.model';
import { ReservationItem } from '../models/reservation-item.model';
import { AuthService } from '../services/auth.service';
import { FirestoreService } from '../services/firestore.service';
import { Observable, combineLatest, filter, map, shareReplay, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EditionService {
  auth = inject(AuthService);
  private firestore = inject(FirestoreService);
  private environment = inject(ENVIRONMENT);

  public edition$ = this.firestore.findDocument<Edition>(
    COLLECTIONS.EDITIONS,
    where('isActive', '==', true),
    where('type', '==', this.environment.appType),
  );

  public currentEdition$ = this.edition$.pipe(
    filter((edition): edition is Edition => !!edition),
    map((edition) => this.prepareDates(edition)),
  );

  premiumPack$ = this.currentEdition$.pipe(map((edition) => edition.premiumPacks));

  public editionStages$ = this.currentEdition$.pipe(
    switchMap((edition) =>
      this.firestore.listDocumentsWithId<Stage>(
        [COLLECTIONS.EDITIONS, edition.id, COLLECTIONS.EDITION_STAGES].join('/'),
        orderBy('order', 'asc'),
      ),
    ),
    shareReplay(1),
  );

  public messagesReceived$ = combineLatest([this.currentEdition$, this.auth.uid$]).pipe(
    switchMap(([edition, uid]) =>
      this.firestore.listDocumentsWithId<MessageSended>(
        [COLLECTIONS.EDITIONS, edition.id, COLLECTIONS.EDITION_MESSAGES_SEND].join('/'),
        where('userId', '==', uid),
        orderBy('sentAt', 'asc'),
      ),
    ),
    shareReplay(1),
  );

  public messagesScheduled$ = combineLatest([this.currentEdition$, this.auth.uid$]).pipe(
    switchMap(([edition, uid]) =>
      this.firestore.listDocumentsWithId<MessageScheduled>(
        [COLLECTIONS.EDITIONS, edition.id, COLLECTIONS.EDITION_SCHEDULE_MESSAGES].join('/'),
      ),
    ),
    shareReplay(1),
  );

  public messages$: Observable<MessageWithSchedule[]> = combineLatest([
    this.messagesReceived$,
    this.messagesScheduled$,
  ]).pipe(
    map(([sentMessages, scheduledMessages]) => {
      const scheduledMap = new Map(scheduledMessages.map((m) => [m.id, m]));
      console.log('scheduledMap', scheduledMap);
      return sentMessages.map((sent) => {
        const scheduledInfo = scheduledMap.get(sent.messageScheduleId);
        return {
          ...sent,
          scheduled: scheduledInfo ?? null,
        } as MessageWithSchedule;
      });
    }),
    shareReplay(1),
  );

  public reservationItems$ = this.currentEdition$.pipe(
    switchMap((edition: Edition) =>
      this.firestore.listDocumentsWithId<ReservationItem>(
        COLLECTIONS.RESERVATION_ITEMS,
        where('editionId', '==', edition.id),
      ),
    ),
    shareReplay(1),
  );

  private cachedItem: Record<Id, Observable<ReservationItem>> = {};
  getItem(itemId: string) {
    if (!this.cachedItem[itemId]) {
      this.cachedItem[itemId] = this.firestore.getDocument<ReservationItem>(COLLECTIONS.RESERVATION_ITEMS, itemId);
    }
    return this.cachedItem[itemId];
  }

  private cachedStage: Record<Id, Observable<Stage>> = {};
  getStage(stageId: string): Observable<Stage> {
    if (!this.cachedStage[stageId]) {
      this.cachedStage[stageId] = this.currentEdition$.pipe(
        switchMap((edition: Edition) =>
          this.firestore
            .getDocument<Stage>([COLLECTIONS.EDITIONS, edition.id, COLLECTIONS.EDITION_STAGES].join('/'), stageId)
            .pipe(
              map((stage: Stage) => ({
                ...stage,
                editionId: edition.id,
              })),
            ),
        ),
      );
    }
    return this.cachedStage[stageId];
  }

  markMessageAsRead(messageId: string, editionId: string) {
    this.firestore.update<MessageSended>(
      [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_MESSAGES_SEND].join('/'),
      { id: messageId, read: true, readAt: new Date() },
    );
  }

  private prepareDates(edition: Edition) {
    const convertedEdition = {
      ...edition,
      rates: edition.rates.map((rate) => {
        return {
          ...rate,
          startDate: new Date(rate.startDate),
          endDate: new Date(rate.endDate),
        };
      }),
    };
    return convertedEdition;
  }
}
